import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {PaymentInputsWrapper, usePaymentInputs} from 'react-payment-inputs';
import images from 'react-payment-inputs/images';
import {Box, CircularProgress, FormControlLabel, Radio, RadioGroup} from '@mui/material';
import SubscriptionApiService from '../../../api/SubscriptionApiService';
import CardPaymentDto from '../../../DTOs/Subscription/CardPaymentDto';
import AppDialog from '../AppDialog';

const NEW_PAYMENT_METHOD_ID = 0;

export default function ChangePaymentModal(props) {
    const { open, userId, subscription, setSnackbarProperties, close, isMobile } = props;

    const[currentPaymentOptions, setCurrentPaymentOptions] = useState([]);
    const[currentSelectedPaymentOption, setCurrentSelectedPaymentOption] = useState();
    const[cardNumber, setCardNumber] = useState('');
    const[expirationDate, setExpirationDate] = useState('');
    const[cvc, setCvc] = useState('');
    const[loading, setLoading] = useState(false);
    const[saving, setSaving] = useState(false);

    const {
        wrapperProps,
        getCardImageProps,
        getCardNumberProps,
        getExpiryDateProps,
        getCVCProps
    } = usePaymentInputs();

    const isNewPaymentMethodSelected = useMemo(
        () => currentSelectedPaymentOption === NEW_PAYMENT_METHOD_ID,
        [currentSelectedPaymentOption]
    );

    const subscriptionApiService = new SubscriptionApiService();

    async function savePaymentInfo() {
        setSaving(true);

        if(isNewPaymentMethodSelected) {
            const newPaymentCard = new CardPaymentDto();
            newPaymentCard.cardNumber = cardNumber;
            newPaymentCard.cardExpiration = expirationDate;
            newPaymentCard.cvv = cvc;
            const addResponse = await subscriptionApiService.addPaymentMethod(subscription.id, newPaymentCard);
            setSaving(false);

            if(addResponse.success) {
                setSnackbarProperties('Successfully added payment method');
                closeModal();
            } else {
                setSnackbarProperties(`Error adding payment method: ${addResponse.message}`, 'error');
            }
        } else {
            const methodToSave = currentPaymentOptions.find(
                (value) => value.paymentProfileId === currentSelectedPaymentOption?.toString()
            );

            if(!methodToSave) {
                setSaving(false);
                setSnackbarProperties('Please choose a payment method', 'warning');
                return;
            }

            const updateResponse = await subscriptionApiService.updatePaymentMethod(subscription.id, methodToSave.paymentProfileId);
            setSaving(false);

            if(updateResponse.success) {
                setSnackbarProperties('Successfully updated payment method');
                closeModal();
            } else {
                setSnackbarProperties(`Error updating payment method: ${updateResponse.message}`, 'error');
            }
        }
    }

    function handleCardNumberChange(event) {
        setCardNumber(event.target.value);
    }

    function handleExpirationDateChange(event) {
        setExpirationDate(event.target.value);
    }

    function handleCVCChange(event) {
        setCvc(event.target.value);
    }

    function closeModal() {
        setCardNumber('');
        setExpirationDate('');
        setCvc('');
        close();
    }

    async function getUserPaymentInfo() {
        const methods = await subscriptionApiService.getUserPaymentMethods(userId);
        setCurrentPaymentOptions(methods.data);
    }

    useEffect(() => {
        if(open) {
            setLoading(true);
            getUserPaymentInfo().then(() => {
                setCurrentSelectedPaymentOption(parseInt(subscription.paymentProfileId));
                setLoading(false);
            });
        }
    }, [open]);

    const newCardInputs = isMobile
        ? <div>
            {cardNumber.length > 0 && <svg className={'mobile-card-image'} {...getCardImageProps({images})} />}
            <input className={'mobile-card-number-input'} {...getCardNumberProps({onChange: handleCardNumberChange})} value={cardNumber} />
            <input className={'mobile-exp-date-input'} {...getExpiryDateProps({onChange: handleExpirationDateChange})} value={expirationDate} />
            <input className={'mobile-cvc-input'} {...getCVCProps({onChange: handleCVCChange})} value={cvc}/>
        </div>
        : <PaymentInputsWrapper className={'change-payment-card-input-shown'} {...wrapperProps}>
            {cardNumber.length > 0 && <svg {...getCardImageProps({images})} /> }
            <input {...getCardNumberProps({onChange: handleCardNumberChange})} />
            <input {...getExpiryDateProps({onChange: handleExpirationDateChange})} />
            <input {...getCVCProps({onChange: handleCVCChange})} />
        </PaymentInputsWrapper>;

    return (
        <AppDialog
            open={open}
            onClose={closeModal}
            title='Change Payment Method'
            onSubmit={savePaymentInfo}
            submitText='Save'
            submitDisabled={loading || !Number.isInteger(currentSelectedPaymentOption)}
            busy={saving}
        >
            {loading
                ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
                : <RadioGroup
                    value={currentSelectedPaymentOption ?? ''}
                    onChange={(event) => setCurrentSelectedPaymentOption(parseInt(event.target.value))}
                >
                    {currentPaymentOptions.map((paymentOption, index) =>
                        <FormControlLabel
                            className={paymentOption.isExpired ? 'expired-card-option' : ''}
                            key={index}
                            value={parseInt(paymentOption.paymentProfileId)}
                            control={<Radio
                                checked={currentSelectedPaymentOption === parseInt(paymentOption.paymentProfileId)}
                                disabled={paymentOption.isExpired}
                            />}
                            label={`${paymentOption.cardType} - ${paymentOption.cardNumber} ${paymentOption.isExpired ? '- EXPIRED' : '' }`}
                        />
                    )}
                    <FormControlLabel control={<Radio/>} label={'New payment option'} value={NEW_PAYMENT_METHOD_ID}/>
                    {isNewPaymentMethodSelected && newCardInputs}
                </RadioGroup>
            }
        </AppDialog>
    );
}

ChangePaymentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    userId: PropTypes.number.isRequired,
    subscription: PropTypes.shape({
        id: PropTypes.number,
        paymentProfileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    setSnackbarProperties: PropTypes.func,
    close: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
};